import { useRef, useState } from 'react'
import { prepareFile, type CapturedPage } from '@core/capture'
import { categoriesFor } from '@core/categories'
import { newIdempotencyKey, queueSend } from '@core/documents'
import { parseAmountToCents } from '@core/money'
import { recordExpense } from '@core/expenses'
import type { Space } from '@core/session'
import { Button, Card, ErrorNote, Field } from '@design/components'
import { todayIso } from '@core/dates'
import { Overlay } from '@design/Overlay'

/**
 * A receipt, and what it cost.
 *
 * The amount is typed rather than read from the photo, and that is a
 * deliberate step back from how invoices work. A crumpled thermal receipt is
 * the worst input the extraction model gets, the amounts are small, and you
 * are standing there holding it — typing "23,50" is faster and more reliable
 * than photographing it and correcting the guess afterwards.
 *
 * The photo still goes up. It is the proof, and proof is the whole reason to
 * bother.
 */
export function ExpenseForm({
  space,
  userId,
  onDone,
  onCancel,
}: {
  space: Space
  userId: string
  onDone: () => void
  onCancel: () => void
}) {
  const today = todayIso()
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [spentOn, setSpentOn] = useState(today)
  const [category, setCategory] = useState<string | null>(null)
  const [photo, setPhoto] = useState<CapturedPage | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  async function save() {
    const cents = parseAmountToCents(amount)
    if (cents === null || cents <= 0) {
      setError('Vul een bedrag in.')
      return
    }
    setBusy(true)
    setError('')
    try {
      // The photo is queued the same way a parent's send is: if there is no
      // signal it waits, and the expense is recorded either way. An expense
      // that fails to save because of a photo would be the wrong trade.
      //
      // The key is made here, before either call, because it is the only name
      // the two of them can share for a document that does not exist yet. The
      // expense writes it down as "I am waiting for this"; the upload creates
      // the document under it; a trigger in 0032 ties them together, whichever
      // arrives first.
      const receiptKey = photo ? newIdempotencyKey() : null

      if (photo) {
        await queueSend({
          spaceId: space.id,
          uploadedBy: userId,
          pages: [photo.blob],
          source: 'file',
          idempotencyKey: receiptKey!,
        })
      }

      await recordExpense({
        spaceId: space.id,
        description,
        amountCents: cents,
        spentOn,
        category,
        documentId: null,
        receiptKey,
      })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan lukte niet.')
      setBusy(false)
    }
  }

  return (
    <Overlay>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        capture="environment"
        onChange={async (e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          try {
            setPhoto(await prepareFile(file))
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Die foto lukte niet.')
          }
        }}
        className="hidden"
      />

      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onCancel} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">Uitgave in {space.name}</span>
        <span className="w-20" />
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <Field
          label="Bedrag"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          placeholder="0,00"
          autoFocus
        />

        <Field
          label="Waaraan?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Boodschappen, tram, koffie"
        />

        <Field
          label="Wanneer"
          type="date"
          value={spentOn}
          onChange={(e) => setSpentOn(e.target.value)}
        />

        <p className="text-sm text-muted mb-1.5">Soort</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {categoriesFor(space.kind).map((c) => (
            <button
              key={c}
              onClick={() => setCategory(category === c ? null : c)}
              className={`min-h-[2.5rem] px-3 rounded-xl border text-sm ${
                category === c ? 'border-ink bg-ink/5' : 'border-line text-muted'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <Card className="mb-4">
          <p className="text-sm text-muted mb-2">
            {photo ? 'Bonnetje toegevoegd.' : 'Een bonnetje erbij? Mag ook zonder.'}
          </p>
          <Button tone="secondary" onClick={() => inputRef.current?.click()}>
            {photo ? 'Andere foto' : 'Foto maken'}
          </Button>
        </Card>

        <ErrorNote>{error}</ErrorNote>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={save} disabled={busy}>
          {busy ? 'Bezig…' : 'Opslaan'}
        </Button>
      </div>
    </Overlay>
  )
}
